import { Alerta, Button, DataList, Dialog, Row, Spinner, Stack, TextField } from "@/components";
import type { IndiceCac } from "@/lib/api";
import { etiquetaPeriodo, formatIndice } from "@/lib/cac";
import { useCacTab } from "./useCacTab";

export function CacTab() {
  const t = useCacTab();
  const d = t.dialogo;
  const editando = d?.tipo === "form" && d.indice !== null;

  const columnas = [
    { clave: "mes", titulo: "Mes", render: (i: IndiceCac) => etiquetaPeriodo(i.periodo) },
    { clave: "valor", titulo: "Valor", render: (i: IndiceCac) => formatIndice(i.valor) },
  ];

  const acciones = (i: IndiceCac) => (
    <>
      <Button variante="secundario" tamanio="sm" aria-label={`Editar ${etiquetaPeriodo(i.periodo)}`} onClick={() => t.abrirEditar(i)}>
        Editar
      </Button>
      <Button variante="fantasma" tamanio="sm" aria-label={`Borrar ${etiquetaPeriodo(i.periodo)}`} onClick={() => t.abrirBorrar(i)}>
        Borrar
      </Button>
    </>
  );

  return (
    <Stack espacio={6}>
      <Row justificar="entre" envolver espacio={3}>
        <h1 className="text-pantalla font-light tracking-[-0.03em]">Índice CAC</h1>
        {t.esAdmin && <Button onClick={t.abrirNuevo}>Cargar mes</Button>}
      </Row>
      {t.esAdmin ? (
        <p className="text-chico text-texto-suave">
          El INDEC publica el CAC con un mes de demora: cargá cada mes cuando salga. Las cuotas de un mes sin índice se muestran provisorias con el
          último cargado; si se cobran así, quedan con ese índice aunque después se cargue el del mes.
        </p>
      ) : (
        <p className="text-chico text-texto-suave">Índice de la Cámara Argentina de la Construcción que ajusta las cuotas en pesos.</p>
      )}
      {t.cargando && (
        <div className="grid place-items-center py-10 text-primario">
          <Spinner />
        </div>
      )}
      {t.errorCarga && (
        <Stack espacio={3}>
          <Alerta texto={t.errorCarga} />
          <div>
            <Button variante="secundario" onClick={t.reintentar}>
              Reintentar
            </Button>
          </div>
        </Stack>
      )}
      {!t.cargando && !t.errorCarga && (
        <DataList
          etiqueta="Índices cargados"
          columnas={columnas}
          filas={t.indices}
          claveFila={(i) => i.periodo}
          acciones={t.esAdmin ? acciones : undefined}
          vacio="Todavía no hay índices cargados."
        />
      )}

      <Dialog abierto={d?.tipo === "form"} onCambio={(abierto) => !abierto && t.cerrar()} titulo={editando ? "Editar índice CAC" : "Cargar índice CAC"}>
        <form onSubmit={(e) => void t.guardar(e)} noValidate>
          <Stack espacio={4}>
            <TextField
              etiqueta="Mes"
              placeholder="AAAA-MM"
              inputMode="numeric"
              value={t.form.periodo}
              onChange={(e) => t.cambiar("periodo", e.target.value)}
              error={t.errores.periodo}
              disabled={editando}
            />
            <TextField etiqueta="Valor" inputMode="decimal" value={t.form.valor} onChange={(e) => t.cambiar("valor", e.target.value)} error={t.errores.valor} />
            <Alerta tono="alerta" texto={t.avisoReemplazo} />
            <Alerta texto={t.errorGuardar} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrar}>
                Cancelar
              </Button>
              <Button type="submit" cargando={t.guardando}>
                {t.textoGuardar}
              </Button>
            </Row>
          </Stack>
        </form>
      </Dialog>

      <Dialog abierto={d?.tipo === "borrar"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Borrar índice">
        {d?.tipo === "borrar" && (
          <Stack espacio={4}>
            <p>¿Borrar el índice de {etiquetaPeriodo(d.indice.periodo)}? Si alguna venta ya lo usa, no se puede borrar.</p>
            <Alerta texto={t.errorBorrar} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrar}>
                Cancelar
              </Button>
              <Button variante="peligro" cargando={t.borrando} onClick={() => void t.confirmarBorrar()}>
                Borrar
              </Button>
            </Row>
          </Stack>
        )}
      </Dialog>
    </Stack>
  );
}
